import {
  CustomTooltip,
  NavigationSidebarItem,
  NavigationSidebarItemsGroup,
} from "@masterschool/ui"
import {
  Chip,
  Divider,
  IconButton,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material"

// Fade-at-the-cut scroll affordance: the sand-coloured (sand25 = 245,245,240)
// local-attachment covers slide over the shadow at whichever edge has no more
// content, so a fade shows only where the list actually continues past the edge.
const railScrollSx = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    borderRadius: "3px",
  },
  background: `
    linear-gradient(rgba(245, 245, 240, 1) 30%, rgba(245, 245, 240, 0)) center top,
    linear-gradient(rgba(245, 245, 240, 0), rgba(245, 245, 240, 1) 70%) center bottom,
    radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0)) center top,
    radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0)) center bottom`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "100% 32px, 100% 32px, 100% 12px, 100% 12px",
  backgroundAttachment: "local, local, scroll, scroll",
} as const

export const NavigationRail = ({
  header,
  items,
  isExpanded,
  onToggle,
}: {
  header: React.ReactNode
  items: (NavigationSidebarItem | NavigationSidebarItemsGroup)[]
  isExpanded: boolean
  onToggle: () => void
}) => (
  <Stack height={1} width={1} overflow="hidden">
    <Stack>{header}</Stack>
    <Stack sx={railScrollSx}>
      {items.map((item, index) =>
        item.type === "group-item" ? (
          <RailGroup key={index} group={item} isExpanded={isExpanded} />
        ) : (
          <RailItem key={index} item={item} isExpanded={isExpanded} />
        ),
      )}
    </Stack>
    <CollapseButton isExpanded={isExpanded} onToggle={onToggle} />
  </Stack>
)

const RailGroup = ({
  group,
  isExpanded,
}: {
  group: NavigationSidebarItemsGroup
  isExpanded: boolean
}) => (
  <Stack pt={2}>
    {isExpanded ? (
      <Typography variant="body3" py={1} px={3} color="text.secondary">
        {group.title}
      </Typography>
    ) : (
      // Captions collapse to a subtle divider on the compact rail.
      <Divider sx={{ borderColor: "eTypes.sand50", mx: "16px", mb: "8px" }} />
    )}
    {group.items.map((item, index) => (
      <RailItem key={index} item={item} isExpanded={isExpanded} />
    ))}
  </Stack>
)

const RailItem = ({
  item,
  isExpanded,
}: {
  item: NavigationSidebarItem
  isExpanded: boolean
}) => {
  const icon = (
    <SvgIcon
      component={item.isSelected ? item.selectedIcon : item.unselectedIcon}
      inheritViewBox
      sx={{
        width: 20,
        height: 20,
        color: "primary.main",
        ...(item.isSelected ? { stroke: "none" } : { fill: "none" }),
      }}
    />
  )

  if (isExpanded) {
    return (
      <Stack
        direction="row"
        py={1.75}
        px={3}
        gap={1.5}
        height={48}
        alignItems="center"
        onClick={item.onClick}
        sx={{ cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
      >
        {icon}
        <Typography
          variant={item.isSelected ? "body2_sb" : "body2"}
          sx={{ whiteSpace: "nowrap" }}
        >
          {item.label}
        </Typography>
        {(item.tags ?? []).map((tag, index) => (
          <Chip
            key={index}
            label={tag === "beta" ? "Beta" : "New"}
            size="small"
            sx={{
              height: 18,
              "& .MuiChip-label": { px: 1, typography: "body3" },
            }}
          />
        ))}
      </Stack>
    )
  }

  // Compact rail: icon only, centered; the full name is the hover tooltip
  // (Mayer — a 10px label in the 80px rail truncated mid-word and read as
  // noise).
  return (
    <CustomTooltip
      title={item.label}
      placement="right"
      disableInteractive
      leaveDelay={0}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        height={48}
        onClick={item.onClick}
        sx={{ cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
      >
        {icon}
      </Stack>
    </CustomTooltip>
  )
}

// The collapse/expand control is a small, always-visible button pinned at the
// bottom of the rail (Mayer — the full-height edge strip was poor UX). A quiet
// chevron on the rail's own surface: bottom-right gutter when expanded, centred
// in the lean rail when collapsed. Clicking toggles the expanded state
// (persisted).
const CollapseButton = ({
  isExpanded,
  onToggle,
}: {
  isExpanded: boolean
  onToggle: () => void
}) => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{
      flexShrink: 0,
      py: "10px",
      borderTop: "1px solid",
      borderColor: "eTypes.sand50",
      justifyContent: isExpanded ? "flex-end" : "center",
      pr: isExpanded ? "18px" : 0,
    }}
  >
    <CustomTooltip
      title={isExpanded ? "Collapse" : "Expand"}
      placement="right"
      leaveDelay={100}
    >
      <IconButton
        aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
        onClick={onToggle}
        sx={{
          width: "32px",
          height: "32px",
          color: "text.secondary",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            color: "text.primary",
          },
        }}
      >
        {/* A single thin chevron — kept byte-identical to the campus rail by
            inlining the path here rather than leaning on either app's icon set
            (Mayer: same icon on both apps). */}
        <SvgIcon
          sx={{
            width: 16,
            height: 16,
            fill: "none",
            stroke: "currentColor",
            strokeWidth: 1.75,
            strokeLinecap: "round",
            strokeLinejoin: "round",
            transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
            transition: "transform 0.3s ease-in-out",
          }}
        >
          <polyline points="15 18 9 12 15 6" />
        </SvgIcon>
      </IconButton>
    </CustomTooltip>
  </Stack>
)
